/**
 * createArchivedFilter — фабрика фильтра «Архив» для RepositoryTable.
 *
 * Единый Select с двумя опциями (Не архивные / Архивные) и кнопкой очистки.
 * При очистке (X) фильтр снимается — это и есть «Все». URL хранит явный
 * sentinel `'all'`, чтобы при возврате на вкладку повторно не срабатывал
 * defaultValue `'active'`.
 *
 * @example
 * ```ts
 * export const FilterByArchived = createArchivedFilter<DealRepository>({
 *   applyArchived: (repo, archived) => repo.filterByArchived(archived),
 * });
 * ```
 */
import { useEffect, useState } from 'react';
import { Select } from '@mantine/core';
import logger from '@ayocore/platform/common/helpers/logger';
import { useRepositoryFilter } from '@ayocore/platform/client/components/RepositoryTable/useRepositoryFilter';

const ARCHIVE_OPTIONS = [
  { value: 'active', label: 'Не архивные' },
  { value: 'archived', label: 'Архивные' },
];

/** Sentinel: пользователь явно очистил фильтр — перекрывает defaultValue. */
const CLEARED_SENTINEL = 'all';

export interface CreateArchivedFilterOptions<TRepo> {
  /** Ключ фильтра. По умолчанию 'archived'. */
  key?: string;
  /** Колбек применения boolean-фильтра к репозиторию. */
  applyArchived: (repo: TRepo, archived: boolean) => TRepo;
  /** Ширина селекта в px. По умолчанию 140. */
  width?: number;
  /** Метка для логов (имя модуля). */
  logLabel?: string;
}

export function createArchivedFilter<TRepo>(opts: CreateArchivedFilterOptions<TRepo>) {
  const key = opts.key ?? 'archived';
  const width = opts.width ?? 140;
  const logLabel = opts.logLabel ?? 'FilterByArchived';

  return useRepositoryFilter<TRepo>(
    key,
    ({ filter, disabled, fromUrl, toUrl }) => {
      const [value, setValue] = useState<string | null>(null);

      useEffect(() => {
        try {
          const raw = fromUrl();
          if (raw === CLEARED_SENTINEL) {
            // Пользователь явно очистил — селект пустой, фильтр не применяем.
            setValue(null);
            filter(null);
          } else if (raw === 'archived') {
            setValue('archived');
            filter(repo => opts.applyArchived(repo, true));
          } else if (raw) {
            setValue('active');
            filter(repo => opts.applyArchived(repo, false));
          } else {
            filter(null);
          }
        } catch (error) {
          logger.warn(`${logLabel}: fromUrl failed`, error);
          filter(null);
        }
      }, [filter, fromUrl]);

      const handleChange = (next: string | null) => {
        if (!next) {
          // X в селекте = явная очистка. Пишем sentinel, чтобы перекрыть defaultValue.
          setValue(null);
          filter(null);
          toUrl(CLEARED_SENTINEL);
          return;
        }
        setValue(next);
        if (next === 'archived') {
          filter(repo => opts.applyArchived(repo, true));
          toUrl('archived');
          return;
        }
        filter(repo => opts.applyArchived(repo, false));
        toUrl('active');
      };

      return (
        <Select
          placeholder='Архив'
          data={ARCHIVE_OPTIONS}
          value={value}
          onChange={handleChange}
          clearable
          size='xs'
          disabled={disabled}
          style={{ minWidth: width }}
        />
      );
    },
    { defaultValue: 'active' }
  );
}
